/**
 * Markdown + LaTeX + code renderer for question bodies, options and solutions.
 *
 * This is the ONLY question renderer in the app — student and admin both go
 * through it, so a paper can never look like one thing while it is being
 * written and another while it is being reviewed.
 *
 * Order matters: math is extracted BEFORE markdown runs. Otherwise the markdown
 * parser mangles LaTeX — `$a_1$` becomes `$a<em>1$` because it reads `_` as
 * emphasis, and `\\` inside a matrix is eaten as an escape.
 *
 * So: pull the math out, leave opaque placeholders, run markdown, then put the
 * KaTeX-rendered HTML back.
 *
 * **Escaping — read this before touching it.** The source is NOT pre-escaped.
 * It used to be, and that was a bug: `marked` escapes code spans and fenced
 * blocks itself, so a pre-escaped `<` arrived as `&lt;`, got escaped a second
 * time to `&amp;lt;`, and the student sat an exam that printed a literal
 * `&lt;` where the code said `<`. Escaping is now done exactly once, by
 * `marked`, at the point each token is rendered.
 *
 * Raw HTML in the source is still never passed through — the `html` renderer
 * below escapes it instead — and link and image URLs are restricted to a
 * scheme allowlist. Questions are authored by the admin and by a model via the
 * generate endpoint; letting model output put script into the exam page is not
 * a risk worth taking for a feature nobody needs. Those two rules together are
 * what make the `dangerouslySetInnerHTML` in `Markdown.tsx` safe, and neither
 * may be relaxed without revisiting that.
 */
import { Marked, type Tokens } from "marked";
import katex from "katex";
import hljs from "highlight.js/lib/core";

import bash from "highlight.js/lib/languages/bash";
import c from "highlight.js/lib/languages/c";
import cpp from "highlight.js/lib/languages/cpp";
import csharp from "highlight.js/lib/languages/csharp";
import css from "highlight.js/lib/languages/css";
import go from "highlight.js/lib/languages/go";
import java from "highlight.js/lib/languages/java";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import kotlin from "highlight.js/lib/languages/kotlin";
import matlab from "highlight.js/lib/languages/matlab";
import php from "highlight.js/lib/languages/php";
import plaintext from "highlight.js/lib/languages/plaintext";
import python from "highlight.js/lib/languages/python";
import r from "highlight.js/lib/languages/r";
import ruby from "highlight.js/lib/languages/ruby";
import rust from "highlight.js/lib/languages/rust";
import sql from "highlight.js/lib/languages/sql";
import swift from "highlight.js/lib/languages/swift";
import typescript from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import yaml from "highlight.js/lib/languages/yaml";

/**
 * Registered one by one rather than via `highlight.js` (the all-languages
 * bundle) — that entry point is ~1MB and the exam runner is loaded over a
 * student's home connection. Each module also registers its own aliases, so
 * `js`, `py`, `sh`, `html` and friends resolve without a table here.
 */
for (const [name, language] of [
  ["bash", bash],
  ["c", c],
  ["cpp", cpp],
  ["csharp", csharp],
  ["css", css],
  ["go", go],
  ["java", java],
  ["javascript", javascript],
  ["json", json],
  ["kotlin", kotlin],
  ["matlab", matlab],
  ["php", php],
  ["plaintext", plaintext],
  ["python", python],
  ["r", r],
  ["ruby", ruby],
  ["rust", rust],
  ["sql", sql],
  ["swift", swift],
  ["typescript", typescript],
  ["xml", xml],
  ["yaml", yaml],
] as const) {
  hljs.registerLanguage(name, language);
}

/** What the corner label on a fenced block says, per resolved language. */
const LANGUAGE_LABEL: Record<string, string> = {
  bash: "Shell",
  c: "C",
  cpp: "C++",
  csharp: "C#",
  css: "CSS",
  go: "Go",
  java: "Java",
  javascript: "JavaScript",
  json: "JSON",
  kotlin: "Kotlin",
  matlab: "MATLAB",
  php: "PHP",
  python: "Python",
  r: "R",
  ruby: "Ruby",
  rust: "Rust",
  sql: "SQL",
  swift: "Swift",
  typescript: "TypeScript",
  xml: "HTML",
  yaml: "YAML",
};

interface MathSpan {
  placeholder: string;
  tex: string;
  display: boolean;
}

/** Unlikely to appear in a question, and inert through the markdown parser. */
function placeholderFor(i: number): string {
  return ` MATH${i} `;
}

/**
 * Which lines are code, and therefore off-limits to the math scanner.
 *
 * Fenced blocks were already skipped. Indented blocks were not, so a shell
 * snippet written the four-space way turned `$HOME and $PATH` into a formula —
 * the same class of bug as the escaping one: the question a student is graded
 * on is not the question that was written.
 *
 * The indented-code rule follows CommonMark closely enough for prose: four
 * spaces (or a tab) only opens a code block after a blank line, so an indented
 * continuation line inside a list is not mistaken for one.
 */
function codeLineFlags(lines: readonly string[]): boolean[] {
  const flags = new Array<boolean>(lines.length).fill(false);
  let fence: string | null = null;
  let prevBlank = true;
  let indented = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (fence !== null) {
      flags[i] = true;
      if (trimmed.startsWith(fence)) fence = null;
      prevBlank = false;
      continue;
    }

    const opening = /^\s*(```+|~~~+)/.exec(line);
    if (opening) {
      fence = opening[1]!;
      flags[i] = true;
      prevBlank = false;
      indented = false;
      continue;
    }

    const blank = trimmed === "";
    if (!blank && /^(\t| {4})/.test(line) && (prevBlank || indented)) {
      indented = true;
      flags[i] = true;
    } else if (!blank) {
      indented = false;
    }
    prevBlank = blank;
  }

  return flags;
}

/**
 * Is this `$` opening a formula, or is it a currency symbol?
 *
 * Without asking, `"A shirt costs $5 and a hat costs $7"` renders the words
 * between the two dollars as mathematics. On a quantitative paper that is not
 * an edge case, it is most of the paper. These are KaTeX's own auto-render
 * heuristics:
 *
 *   - the character after the opening `$` is not whitespace
 *   - the character before the closing `$` is not whitespace
 *   - the character after the closing `$` is not a digit — this is the one
 *     that rejects `$5 … $7`
 *   - the span does not cross a blank line, since no formula does
 *
 * A `$` that fails these is emitted literally, which is what a price is.
 */
function inlineMathEnd(src: string, start: number): number {
  if (/\s/.test(src[start] ?? " ")) return -1;

  for (let i = start; i < src.length; i++) {
    const ch = src[i]!;
    if (ch === "\\") {
      i++; // an escaped character cannot be the delimiter
      continue;
    }
    if (ch === "\n" && /^[ \t]*\n/.test(src.slice(i + 1))) return -1; // blank line
    if (ch !== "$") continue;

    if (i === start) return -1; // `$$` is not an inline span
    if (/\s/.test(src[i - 1] ?? " ")) continue; // "$ 5$" — keep looking
    if (/\d/.test(src[i + 1] ?? "")) return -1; // "$5 … $7" — currency
    return i;
  }
  return -1;
}

/** Display math may wrap, but never across a blank line. */
function displayMathEnd(src: string, start: number): number {
  const end = src.indexOf("$$", start);
  if (end === -1) return -1;
  if (/\n[ \t]*\n/.test(src.slice(start, end))) return -1;
  return end;
}

/**
 * Extracts `$$…$$` (display) and `$…$` (inline) spans.
 *
 * `\$` is treated as a literal dollar sign, so prices and currency in a
 * question body do not accidentally open a math span — and so, now, is a `$`
 * that does not look like a delimiter, since nobody escapes their prices.
 */
function extractMath(src: string): { text: string; spans: MathSpan[] } {
  const spans: MathSpan[] = [];
  const isCodeLine = codeLineFlags(src.split("\n"));
  let out = "";
  let i = 0;
  let line = 0;

  while (i < src.length) {
    const ch = src[i];

    if (ch === "\n") {
      out += ch;
      i += 1;
      line += 1;
      continue;
    }

    // Fenced and indented code are not math; copy them through verbatim.
    if (isCodeLine[line]) {
      out += ch;
      i += 1;
      continue;
    }

    // Escaped dollar — emit literally and skip the escape.
    if (ch === "\\" && src[i + 1] === "$") {
      out += "$";
      i += 2;
      continue;
    }

    // Inline code spans are not math either.
    if (ch === "`") {
      const end = src.indexOf("`", i + 1);
      if (end === -1) {
        out += src.slice(i);
        break;
      }
      const run = src.slice(i, end + 1);
      out += run;
      line += (run.match(/\n/g) ?? []).length;
      i = end + 1;
      continue;
    }

    if (ch === "$") {
      const display = src.startsWith("$$", i);
      const start = i + (display ? 2 : 1);
      const end = display
        ? displayMathEnd(src, start)
        : inlineMathEnd(src, start);

      if (end !== -1) {
        const tex = src.slice(start, end);
        const placeholder = placeholderFor(spans.length);
        spans.push({ placeholder, tex, display });
        out += placeholder;
        line += (tex.match(/\n/g) ?? []).length;
        i = end + (display ? 2 : 1);
        continue;
      }
      // Not a delimiter — a dollar sign.
    }

    out += ch;
    i += 1;
  }

  return { text: out, spans };
}

function escapeHtml(src: string): string {
  return src
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderTex(span: MathSpan): string {
  try {
    return katex.renderToString(span.tex, {
      displayMode: span.display,
      throwOnError: false,
      // Renders the offending source in red rather than blowing up the whole
      // question — a broken formula must never hide the rest of the paper.
      errorColor: "#b91c1c",
      strict: false,
      trust: false,
      output: "html",
    });
  } catch {
    return `<code>${escapeHtml(span.tex)}</code>`;
  }
}

/**
 * Highlights a fenced block. An unknown or missing language is not an error —
 * plenty of questions fence pseudocode, sample input or a data table — it just
 * renders unhighlighted rather than guessing a language and colouring it wrong.
 */
function renderCode(text: string, lang: string | undefined): string {
  const requested = (lang ?? "").trim().split(/\s+/)[0]?.toLowerCase() ?? "";
  const resolved = requested ? hljs.getLanguage(requested) : undefined;
  const name = resolved ? (resolved.name ?? requested) : null;

  let body: string;
  if (requested && resolved) {
    try {
      // `ignoreIllegals` — a question deliberately containing broken code (the
      // whole point of "what does this print?") must still render.
      body = hljs.highlight(text, { language: requested, ignoreIllegals: true })
        .value;
    } catch {
      body = escapeHtml(text);
    }
  } else {
    body = escapeHtml(text);
  }

  // Canonical name from highlight.js is prettier than the raw fence tag, but
  // the curated table wins where it disagrees (`xml` → "HTML").
  const label = requested
    ? (LANGUAGE_LABEL[requested] ?? (name ? LANGUAGE_LABEL[name.toLowerCase()] ?? name : null))
    : null;

  const attrs = [
    'class="code-block"',
    label ? `data-lang="${escapeHtml(label)}"` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const codeClass = requested && resolved ? `hljs language-${escapeHtml(requested)}` : "hljs";
  return `<pre ${attrs}><code class="${codeClass}">${body}</code></pre>\n`;
}

/**
 * Schemes a question body may link to or embed.
 *
 * Blocking raw `<script>` tags but leaving hrefs alone is not a defence:
 * `[click](javascript:alert(1))` is ordinary markdown, and `marked`'s own URL
 * cleaner does not reject it. `data:` is allowed only for images, so a figure
 * can still be inlined, and never for a link.
 */
const SAFE_LINK_SCHEMES = ["http:", "https:", "mailto:"];

function safeUrl(href: string, allowDataImage: boolean): string | null {
  const value = href.trim();
  // Relative and anchor URLs carry no scheme and cannot execute.
  if (value === "" || /^[#/?]/.test(value)) return value === "" ? null : value;

  let url: URL;
  try {
    url = new URL(value, "https://exam.invalid/");
  } catch {
    return null;
  }

  if (SAFE_LINK_SCHEMES.includes(url.protocol)) return value;
  if (
    allowDataImage &&
    url.protocol === "data:" &&
    /^data:image\/(png|jpeg|gif|webp|svg\+xml);/i.test(value)
  ) {
    return value;
  }
  return null;
}

/**
 * A private `Marked` instance — `marked.use()` mutates a module-level singleton,
 * so configuring the shared export would leak these renderers into any other
 * caller of `marked` in the app.
 */
const md = new Marked({ gfm: true, breaks: false, async: false });

md.use({
  renderer: {
    code({ text, lang }: Tokens.Code): string {
      return renderCode(text, lang);
    },
    /** Raw HTML is shown as text, never parsed. See the file header. */
    html({ text }: Tokens.HTML | Tokens.Tag): string {
      return escapeHtml(text);
    },

    /** A link to a scheme we do not trust renders as its own text. */
    link({ href, title, tokens }: Tokens.Link): string {
      const inner = this.parser!.parseInline(tokens);
      const url = safeUrl(href, false);
      if (url === null) return inner;
      const attrs = title ? ` title="${escapeHtml(title)}"` : "";
      return `<a href="${escapeHtml(url)}"${attrs} target="_blank" rel="noreferrer noopener">${inner}</a>`;
    },

    /** Same for an image: an untrusted src degrades to the alt text. */
    image({ href, title, text }: Tokens.Image): string {
      const url = safeUrl(href, true);
      if (url === null) return escapeHtml(text);
      const attrs = title ? ` title="${escapeHtml(title)}"` : "";
      return `<img src="${escapeHtml(url)}" alt="${escapeHtml(text)}"${attrs}>`;
    },
  },
});

/**
 * Renders markdown + LaTeX to an HTML string.
 *
 * Cached: the exam runner re-renders on every keystroke in a NAT field and on
 * every palette hover, and neither KaTeX nor highlight.js is cheap.
 */
const cache = new Map<string, string>();
const CACHE_LIMIT = 500;

export function renderQuestionHtml(source: string): string {
  const cached = cache.get(source);
  if (cached !== undefined) return cached;

  const { text, spans } = extractMath(source);

  let html = md.parse(text) as string;

  for (const span of spans) {
    // The placeholder may have been HTML-escaped or wrapped by marked; a plain
    // split/join is enough because the sentinel survives both.
    html = html.split(span.placeholder).join(renderTex(span));
  }

  if (cache.size >= CACHE_LIMIT) cache.clear();
  cache.set(source, html);
  return html;
}

/** Exported for the renderer's own tests. */
export const __test = { extractMath, renderCode };
